import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// "Additional Orders" is a free-text note field rather than a headcount —
// every other leaf stays a numeric-only count.
const isFreeTextNode = (title) => title?.trim().toLowerCase() === "additional orders";

// Recursively renders the "advanced" client layout tree for order entry:
// group nodes become collapsible sections, service (leaf) nodes get a count input.
export default function LayoutOrderTree({ nodes, counts, onCountChange, editable, parentKey = "" }) {
  const list = nodes || [];

  if (list.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        This company has no layout on file.
      </Typography>
    );
  }

  // Consecutive leaf (service) nodes are batched into a single flex-wrap row
  // so they lay out like ServiceCountsGrid; group nodes stay full-width
  // accordions and break the current row.
  const rows = [];
  let currentLeafRow = [];
  list.forEach((node) => {
    const isGroup = !!node.children && node.children.length > 0;
    if (isGroup) {
      if (currentLeafRow.length) {
        rows.push({ type: "leaves", nodes: currentLeafRow });
        currentLeafRow = [];
      }
      rows.push({ type: "group", node });
    } else {
      currentLeafRow.push(node);
    }
  });
  if (currentLeafRow.length) {
    rows.push({ type: "leaves", nodes: currentLeafRow });
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {rows.map((row, idx) => {
        if (row.type === "group") {
          const node = row.node;
          const key = parentKey ? `${parentKey}.${node.id}` : node.id;
          return (
            <Accordion key={node.id} defaultExpanded disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">{node.title}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <LayoutOrderTree
                  nodes={node.children}
                  counts={counts}
                  onCountChange={onCountChange}
                  editable={editable}
                  parentKey={key}
                />
              </AccordionDetails>
            </Accordion>
          );
        }

        return (
          <Box key={idx} sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {row.nodes.map((node) => {
              const key = parentKey ? `${parentKey}.${node.id}` : node.id;
              return (
                <Box key={node.id} sx={{ flex: { xs: "1 1 45%", sm: "1 1 45%" } }}>
                  <Typography variant="body2">{node.title}</Typography>
                  <TextField
                    size="small"
                    value={counts[key] ?? (isFreeTextNode(node.title) ? "" : 0)}
                    onChange={(e) => onCountChange(key, e.target.value)}
                    inputProps={isFreeTextNode(node.title) ? undefined : { inputMode: "numeric" }}
                    disabled={!editable}
                    sx={{ width: { xs: "100%", sm: "100%" } }}
                  />
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
}
